import React, { createContext, useCallback, useMemo, useState } from 'react';
import defaultDesignTokenVariables from '@amzn/stencil-design-tokens/js/web/theme/stencilTheme';
import { breakpoints as defaultBreakpoints } from '@amzn/stencil-design-tokens/js/web/utils';
import pxtTheme from '..';
import { mapBreakpoints } from '../adapter/mappers/breakpoints';
import { mergeTheme } from '../adapter/mergeTheme';
import { isDefaultStencilTheme } from '../utils/isDefaultStencilTheme';
import { convertToLegacyTheme, convertToTokens, isLegacyCustomizedTheme, isLegacyDefaultTheme } from '../adapter';
import defaultTheme from '../pxtTheme';
import { selectBreakpoint, selectColor, selectColorOrNull, selectConstant, selectFontFamily, selectFontSize, selectFontWeight, selectLineHeight, selectSpace, selectZIndex, selectRadius } from './themeSelectors';
export const StencilThemeContext = /*#__PURE__*/createContext({
  legacyTheme: defaultTheme,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setLegacyTheme: _ => null,
  // @ts-ignore There's no sensible default for 'selectors'
  selectors: {},
  theme: defaultDesignTokenVariables,
  breakpoints: defaultBreakpoints,
  isLegacyThemeCustomized: false,
  isDefaultStencilThemeCustomized: false
});
export const StencilThemeProvider = _ref => {
  let {
    children,
    theme: externalTheme,
    breakpoints: externalBreakpoints
  } = _ref;
  const isLegacy = isLegacyCustomizedTheme(externalTheme);
  let isLegacyThemeCustomized = false;
  let isDefaultStencilThemeCustomized = false;
  if (isLegacy) {
    isLegacyThemeCustomized = !isLegacyDefaultTheme(externalTheme);
    console.warn("The selectors usage is deprecated and will be removed in 5.x release (Ex: selectors.color(\"neutral90\"), selectors.radius('R100')).\n      Please migrate to using stencil design tokens instead. For more information please check the \n      official docs on stencil new theming system: https://stencil.a2z.com/develop/theming");
  } else {
    isDefaultStencilThemeCustomized = !isDefaultStencilTheme(externalTheme);
  }
  const convertedExternalTheme = isLegacy ? convertToTokens(externalTheme) : externalTheme;
  const {
    mergedTokens
  } = useMemo(() => mergeTheme({
    defaultLegacyTokens: convertToTokens(pxtTheme),
    defaultTokens: defaultDesignTokenVariables,
    customTokens: convertedExternalTheme
  }), [convertedExternalTheme]);
  const {
    legacyTheme: mergedLegacyTheme
  } = convertToLegacyTheme({
    tokens: mergedTokens,
    legacyTheme: isLegacy && externalTheme ? externalTheme : pxtTheme
  });
  const {
    mappedLegacyBreakpoints,
    mappedBreakpoints
  } = mapBreakpoints({
    externalLegacyThemeBreakpoints: isLegacy ? externalTheme === null || externalTheme === void 0 ? void 0 : externalTheme.breakpoint : pxtTheme.breakpoint,
    externalBreakpoints: externalBreakpoints || defaultBreakpoints,
    defaultLegacyBreakpoints: pxtTheme.breakpoint
  });
  mergedLegacyTheme.breakpoint = mappedLegacyBreakpoints;
  const [theme, setTheme] = useState(() => mergedLegacyTheme);
  const tokens = mergedTokens;
  const bindTheme = useCallback(selector => {
    return function () {
      for (var _len = arguments.length, args = new Array(_len), _key = 0; _key < _len; _key++) {
        args[_key] = arguments[_key];
      }
      return selector(theme, ...args);
    };
  }, [theme]);
  const selectors = useMemo(() => ({
    color: bindTheme(selectColor),
    colorOrNull: bindTheme(selectColorOrNull),
    fontFamily: bindTheme(selectFontFamily),
    fontSize: bindTheme(selectFontSize),
    lineHeight: bindTheme(selectLineHeight),
    fontWeight: bindTheme(selectFontWeight),
    space: bindTheme(selectSpace),
    breakpoint: bindTheme(selectBreakpoint),
    zIndex: bindTheme(selectZIndex),
    constant: bindTheme(selectConstant),
    radius: bindTheme(selectRadius)
  }), [bindTheme]);
  const contextValue = useMemo(() => ({
    selectors,
    setLegacyTheme: setTheme,
    legacyTheme: theme,
    theme: tokens,
    breakpoints: mappedBreakpoints,
    isLegacyThemeCustomized,
    isDefaultStencilThemeCustomized
  }), [selectors, setTheme, theme, tokens, mappedBreakpoints, isLegacyThemeCustomized, isDefaultStencilThemeCustomized]);
  return /*#__PURE__*/React.createElement(StencilThemeContext.Provider, {
    value: contextValue
  }, children);
};
StencilThemeContext.displayName = 'StencilThemeContext';